import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { errorHandler, successToast } from "../../Utils/toast";
import { _delete, _get, _patch, _post } from "../../Services/baseApi";
import { UPLOAD_FOLDERS } from "../../Utils/uploadFolders";

// URLS
const USER_LIST_URL = "/user/user-list";
const USER_DETAILS_URL = "/user";
const USER_DELETE_URL = "/user";
const USER_UPDATE_URL = "/user";
const FILE_UPLOAD_URL = "/file-upload";
const USER_CREATE_URL = "/user/user-create";

const userSliceNew = createSlice({
  name: "user",
  initialState: {
    userList: [],
    isLoading: false,
    total: 0,
    userDetails: {},
    sortColumn: "id",
    sortDirection: "DESC",
    page: 1,
    limit: 10,
    uploadFileData: {
      filename: "",
      path: "",
      size: "",
      fileType: "",
    },
  },
  reducers: {
    setUserDetails: (state, action) => {
      state.userDetails = action?.payload;
    },
    setSortColumn: (state, action) => {
      state.sortColumn = action?.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action?.payload;
    },
    setPage: (state, action) => {
      state.page = action?.payload;
    },
    setLimit: (state, action) => {
      state.limit = action?.payload;
    },
  },
  extraReducers: (builder) => {
    // Get all user list
    builder.addCase(getAllUserListThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getAllUserListThunk.fulfilled, (state, action) => {
      state.userList = action?.payload?.findUsers;
      state.total = action?.payload?.totalCount;
      state.userDetails = {};
      state.isLoading = false;
    });
    builder.addCase(getAllUserListThunk.rejected, (state) => {
      state.userList = [];
      state.isLoading = false;
    });

    // Get user details
    builder.addCase(getUserDetailsThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getUserDetailsThunk.fulfilled, (state, action) => {
      state.userDetails = action?.payload;
      state.isLoading = false;
    });
    builder.addCase(getUserDetailsThunk.rejected, (state) => {
      state.userDetails = {};
      state.isLoading = false;
    });

    // Create user
    builder.addCase(createUserThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createUserThunk.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createUserThunk.rejected, (state) => {
      state.isLoading = false;
    });

    // Delete user
    builder.addCase(deleteUserThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deleteUserThunk.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(deleteUserThunk.rejected, (state) => {
      state.isLoading = false;
    });

    // file upload
    builder.addCase(fileUploadThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fileUploadThunk.fulfilled, (state, action) => {
      state.isLoading = false;
      state.uploadFileData = action.payload;
    });
    builder.addCase(fileUploadThunk.rejected, (state) => {
      state.isLoading = false;
    });

    // Update user
    builder.addCase(updateUserThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateUserThunk.fulfilled, (state, action) => {
      state.isLoading = false;
      if (action.payload?.id) {
        state.userDetails = {
          ...state.userDetails,
          ...action.payload,
        };
      }
    });
    builder.addCase(updateUserThunk.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  setUserDetails,
  setSortColumn,
  setSortDirection,
  setPage,
  setLimit,
} = userSliceNew.actions;

// Define the reducer export with persist data
export default persistReducer(
  {
    key: "users/persist",
    storage,
    whitelist: [],
  },
  userSliceNew.reducer,
);

export const getAllUserListThunk = createAsyncThunk(
  "users/getAlluserList",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _get(USER_LIST_URL, {
        page: payload?.page || undefined,
        limit: payload?.limit || undefined,
        name: payload?.searchText || undefined, // default empty if not provided
        key: payload?.sortColumn || undefined,
        keyOrder: payload?.sortDirection || undefined,
        roleId: payload?.roleID || undefined,
        status: payload?.status || undefined,
        type: payload?.type || undefined,
      });
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const deleteUserThunk = createAsyncThunk(
  "users/deleteUser",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _delete(USER_DELETE_URL + `/${payload?.id}`);
      payload?.handleFun();
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const getUserDetailsThunk = createAsyncThunk(
  "users/getUserDetails",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _get(USER_DETAILS_URL + "/" + payload);
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const updateUserThunk = createAsyncThunk(
  "users/updateUser",
  async (payload, { rejectWithValue }) => {
    try {
      const { handleFun, successMessage, ...updateData } = payload;
      const response = await _patch(
        USER_UPDATE_URL + "/" + payload?.id,
        updateData,
      );
      successToast(
        successMessage ||
          response?.data?.message ||
          "Profile updated successfully.",
      );
      handleFun?.();
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const fileUploadThunk = createAsyncThunk(
  "user/file/upload",
  async (payload, { rejectWithValue }) => {
    const file = payload?.files;
    const hasUploadableFile =
      file instanceof File || file instanceof Blob;

    if (!hasUploadableFile) {
      if (payload.isOptional) {
        payload.handleFun?.({});
        return {};
      }
      const error = new Error("Please select a file to upload.");
      errorHandler(error);
      return rejectWithValue(error);
    }

    const indentData = new FormData();
    indentData.append("fileType", payload?.fileType || "image");
    indentData.append("folder", payload?.folder || UPLOAD_FOLDERS.DEFAULT);
    indentData.append("files", file);
    try {
      const response = await _post(FILE_UPLOAD_URL, indentData);
      if (!payload.isOptional && !payload.silent) {
        successToast(response?.data?.message || "File uploaded successfully.");
      }
      payload.handleFun?.(response?.data?.result);
      return response?.data?.result;
    } catch (error) {
      if (!payload.isOptional) {
        errorHandler(error);
      } else {
        payload.handleFun?.({});
      }
      return rejectWithValue(error);
    }
  },
);

export const createUserThunk = createAsyncThunk(
  "users/createUser",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _post(USER_CREATE_URL, payload);
      payload?.handleFun();
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);
